import * as THREE from 'three/webgpu'
import { texture } from 'three/tsl'

const CANVAS_W = 4096
const CANVAS_H = 1024
const PLANE_ASPECT = CANVAS_W / CANVAS_H
const TITLE_TEXT = 'cats & keyboards'
const FILL_FRACTION_LANDSCAPE = 0.82   // fraction of visible floor width the title occupies
const FILL_FRACTION_PORTRAIT = 0.9

// Renders the hero title onto the floor as an emissive Three.js mesh.
// Plane size is computed from real camera fov + viewport aspect so the
// title fills a consistent fraction of the visible ground regardless of
// window size. Re-runs on every resize.
export default class FloorTitle {
  constructor({ scene, camera }) {
    this.scene = scene
    this.camera = camera
    this._mesh = null
    this._lastDpr = null
    this._onResize = () => this.resize()
    window.addEventListener('resize', this._onResize)
    window.addEventListener('orientationchange', this._onResize)

    this._init()
  }

  dispose() {
    window.removeEventListener('resize', this._onResize)
    window.removeEventListener('orientationchange', this._onResize)
    if (this._mesh) {
      this.scene.remove(this._mesh)
      this._mesh.geometry?.dispose()
      this._mesh.material?.dispose?.()
      const tex = this._mesh.material?.map
      if (tex) tex.dispose()
    }
  }

  async _init() {
    try { await document.fonts.ready } catch (_) { /* older browsers */ }

    this._canvas = document.createElement('canvas')
    this._canvas.width = CANVAS_W
    this._canvas.height = CANVAS_H
    this._drawCanvas()

    const tex = new THREE.CanvasTexture(this._canvas)
    tex.colorSpace = THREE.SRGBColorSpace
    tex.anisotropy = 8
    tex.generateMipmaps = true
    tex.minFilter = THREE.LinearMipmapLinearFilter
    tex.magFilter = THREE.LinearFilter
    this._texture = tex

    const mat = new THREE.MeshBasicNodeMaterial({
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide,
      toneMapped: false, // keep amber hot for the bloom pass
    })
    const tNode = texture(tex)
    mat.colorNode = tNode.rgb
    mat.opacityNode = tNode.a

    // Placeholder geometry — resize() swaps it in with real dimensions
    const mesh = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), mat)
    mesh.rotation.x = -Math.PI / 2
    mesh.position.set(0, 0.05, 0)
    mesh.renderOrder = 0
    this._mesh = mesh
    this.scene.add(mesh)

    this.resize()
  }

  _drawCanvas() {
    const ctx = this._canvas.getContext('2d')
    ctx.clearRect(0, 0, CANVAS_W, CANVAS_H)
    ctx.fillStyle = '#ffb057'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.font = `italic 520px "Fraunces", serif`
    ctx.shadowColor = 'rgba(255, 122, 26, 0.55)'
    ctx.shadowBlur = 40
    ctx.fillText(TITLE_TEXT, CANVAS_W / 2, CANVAS_H / 2)
  }

  resize() {
    if (!this._mesh || !this.camera) return
    const w = window.innerWidth || 1
    const h = window.innerHeight || 1
    const vAspect = w / h

    // Find the visible floor width at the plane's z depth using the real camera
    // pose. Works for any fov / camera position without empirical lookup tables.
    const camPos = this.camera.position
    const planeZ = vAspect < 1.0 ? -6 : -2
    const depth = Math.hypot(camPos.x, camPos.y, camPos.z - planeZ)

    const fovY = (this.camera.fov * Math.PI) / 180
    const visibleHalfH = Math.tan(fovY / 2) * depth
    const visibleHalfW = visibleHalfH * vAspect
    const visibleWidth = 2 * visibleHalfW

    const fill = vAspect >= 1.0 ? FILL_FRACTION_LANDSCAPE : FILL_FRACTION_PORTRAIT
    // Clamp so the title never gets absurdly tiny or larger than the grid
    let planeWidth = visibleWidth * fill
    planeWidth = Math.max(16, Math.min(140, planeWidth))
    const planeDepth = planeWidth / PLANE_ASPECT

    // Swap geometry
    this._mesh.geometry.dispose()
    this._mesh.geometry = new THREE.PlaneGeometry(planeWidth, planeDepth)
    this._mesh.position.z = planeZ

    // If DPR changed drastically (e.g. moved to a high-DPI display), the
    // rasterized canvas text could look softer. Re-draw to keep it crisp.
    const dpr = window.devicePixelRatio || 1
    if (this._lastDpr === null || Math.abs(dpr - this._lastDpr) > 0.25) {
      this._drawCanvas()
      if (this._texture) this._texture.needsUpdate = true
      this._lastDpr = dpr
    }
  }
}
